import { useState, useEffect } from 'react';

/**
 * Custom hook for detecting mobile devices and screen sizes
 * @returns {Object} - Mobile detection state and utilities
 */
export function useMobile() {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setScreenSize({ width, height });
      
      // Mobile breakpoint: <= 768px
      const mobile = width <= 768;
      // Tablet breakpoint: 769px - 1024px
      const tablet = width > 768 && width <= 1024;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
    };

    // Check on mount
    checkDeviceType();

    // Add event listener for window resize
    window.addEventListener('resize', checkDeviceType);

    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    screenSize,
    // Utility functions
    isSmallMobile: screenSize.width <= 480,
    isMediumMobile: screenSize.width > 480 && screenSize.width <= 768,
    isLargeMobile: screenSize.width > 768 && screenSize.width <= 1024,
    // Breakpoint helpers
    sm: screenSize.width >= 640,
    md: screenSize.width >= 768,
    lg: screenSize.width >= 1024,
    xl: screenSize.width >= 1280,
    xxl: screenSize.width >= 1536
  };
}

/**
 * Custom hook for responsive behavior
 * @param {Object} breakpoints - Responsive values for different screen sizes
 * @returns {any} - Current value based on screen size
 */
export function useResponsive(breakpoints = {}) {
  const { isMobile, isTablet, screenSize } = useMobile();
  
  const {
    mobile = null,
    tablet = null,
    desktop = null,
    sm = null,
    md = null,
    lg = null,
    xl = null
  } = breakpoints;

  // Return appropriate value based on screen size
  if (screenSize.width >= 1280 && xl !== null) return xl;
  if (screenSize.width >= 1024 && lg !== null) return lg;
  if (screenSize.width >= 768 && md !== null) return md;
  if (screenSize.width >= 640 && sm !== null) return sm;
  
  // Fallback to device type
  if (isMobile && mobile !== null) return mobile;
  if (isTablet && tablet !== null) return tablet;
  if (desktop !== null) return desktop;
  
  // Return first non-null value as fallback
  return mobile || tablet || desktop || sm || md || lg || xl;
}

/**
 * Custom hook for touch device detection
 * @returns {boolean} - Whether device supports touch
 */
export function useTouch() {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouch(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    };

    checkTouch();
  }, []);

  return isTouch;
}

/**
 * Custom hook for orientation detection
 * @returns {Object} - Orientation state
 */
export function useOrientation() {
  const [orientation, setOrientation] = useState({
    isPortrait: true,
    isLandscape: false,
    angle: 0
  });

  useEffect(() => {
    const handleOrientationChange = () => {
      const angle = window.screen?.orientation?.angle || 0;
      const isPortrait = window.innerHeight > window.innerWidth;
      
      setOrientation({
        isPortrait,
        isLandscape: !isPortrait,
        angle
      });
    };

    handleOrientationChange();
    
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  return orientation;
}

export default useMobile;